import { useCustomersByName, useCustomersContext } from '@src/hooks/useCustomer';
import type { DisplayNameEntity, FilterableSelectProperty } from '@talos/kyoko';
import { IconName, compareDisplayNameOrName, getDisplayNameOrNameLabel, useObservableValue } from '@talos/kyoko';
import { useMemo } from 'react';
import { map } from 'rxjs';

interface UseCounterpartyFilterParams {
  /** If true, will only allow the user to select one counterparty. Defaults to false. */
  singleSelect: boolean;
  /** The label to use for the filter. */
  label: string;
  /** If true, will not show the filter until all the options are loaded. */
  nullUntilLoaded?: boolean;
}

export function getCounterpartyFilterOptionLabel(
  name: string,
  counterpartiesByName: Map<string, DisplayNameEntity> | undefined
): string {
  const counterparty = counterpartiesByName?.get(name);
  if (counterparty) {
    return getDisplayNameOrNameLabel(counterparty);
  }
  return name;
}

/**
 * This hook creates the filter property definition for Counterparty properties.
 *
 * @param singleSelect If true, will only allow the user to select one counterparty. Defaults to false.
 * @returns Filter Property definition to use for Counterparty filters
 */
function useFilter({ singleSelect, label, nullUntilLoaded }: UseCounterpartyFilterParams) {
  const { customers: customersObs, customersLoaded } = useCustomersContext();
  const customerNames = useObservableValue(
    () => customersObs.pipe(map(cs => [...cs].toSorted((a, b) => compareDisplayNameOrName(a, b)).map(c => c.Name))),
    [customersObs],
    []
  );
  const customersByName = useCustomersByName();

  return useMemo(() => {
    if (nullUntilLoaded && (!customersLoaded || !customersByName)) {
      return null;
    }
    return {
      ...({
        key: singleSelect ? 'Counterparty' : 'Counterparties',
        label: label,
        field: 'Counterparty',
        icon: IconName.Counterparty,
        control: singleSelect ? 'single-select' : 'multi-select',
        options: customerNames,
        getOptionLabel: name => getCounterpartyFilterOptionLabel(name, customersByName),
        clearUnsupportedSelectionsOnInit: nullUntilLoaded, // if the options are loaded async, this needs to be off
      } as const satisfies FilterableSelectProperty),
    };
  }, [customerNames, customersByName, customersLoaded, label, nullUntilLoaded, singleSelect]);
}

/**
 * Counterparty filter with single-select enabled.
 * @returns Filter Property definition to use for Counterparty filters
 */
export function useCounterpartyFilter({
  label = 'Counterparty',
  nullUntilLoaded = false,
}: Partial<UseCounterpartyFilterParams> = {}) {
  return useFilter({ singleSelect: true, label, nullUntilLoaded }) as FilterableSelectProperty<'Counterparty', string>;
}

/**
 * Counterparty filter with single-select disabled (multi-select).
 * @returns Filter Property definition to use for Counterparties filters
 */
export function useCounterpartiesFilter({
  label = 'Counterparties',
  nullUntilLoaded = false,
}: Partial<UseCounterpartyFilterParams> = {}) {
  return useFilter({
    singleSelect: false,
    label,
    nullUntilLoaded,
  }) as FilterableSelectProperty<'Counterparties', string>;
}
